// src/data/mock/initiative.ts
// Gaza Sports Education Initiative — typed bilingual data

export type Bilingual = { en: string; ar: string };

export interface ScholarshipSource {
  id: 'bihub' | 'bilbao' | 'iknow' | 'champions-hub';
  name: Bilingual;
  count: number;
  platform: Bilingual;
  importance: Bilingual;
  logo?: string;
  logoTreatment?: 'light' | 'dark';
  website?: string;
  highlight?: boolean;
  /** Indicative cost per scholarship in USD. Final pricing confirmed by finance. */
  pricePerUnitUSD: number;
}

export interface InitiativePartner {
  id: string;
  name: Bilingual;
  role: Bilingual;
  scope: Bilingual;
  emphasis: 'primary' | 'co-host' | 'supporter';
}

export interface Phase {
  id: string;
  range: Bilingual;
  title: Bilingual;
  tracks: {
    institutional: Bilingual;
    digital: Bilingual;
    operational: Bilingual;
  };
}

export interface SponsorPackage {
  id: 'diamond' | 'gold' | 'silver';
  name: Bilingual;
  totalGrants: number;
  breakdown: {
    bihub: number;
    bilbao: number;
    iknow: number;
    championsHub: number;
  };
  /** Indicative price in USD computed from breakdown × per-unit cost. */
  priceUSD: number;
  benefits: Bilingual[];
  badge: Bilingual;
  featured?: boolean;
}

export interface InternationalTarget {
  id: string;
  name: Bilingual;
  domain: Bilingual;
  entryPoint: Bilingual;
  status: Bilingual;
}

export interface KpiGroup {
  id: 'operations' | 'impact' | 'sustainability';
  title: Bilingual;
  items: Bilingual[];
}

export interface FaqItem {
  question: Bilingual;
  answer: Bilingual;
}

/**
 * Per-source unit costs in USD. Anchored on champions-hub.com/api/v1/courses
 * median list prices for advanced courses per partner; Champions Hub uses
 * platform-owned content (lower delivery cost). Final pricing confirmed by finance.
 */
export const SCHOLARSHIP_UNIT_PRICE_USD = {
  bihub: 750,
  bilbao: 400,
  iknow: 500,
  championsHub: 150,
} as const;

/** Per-source maximum that the initiative can deliver. Sums to 150. */
export const SCHOLARSHIP_POOL_MAX = {
  bihub: 5,
  bilbao: 30,
  iknow: 30,
  championsHub: 85,
} as const;

export type SponsorBreakdown = {
  bihub: number;
  bilbao: number;
  iknow: number;
  championsHub: number;
};

export function totalGrants(b: SponsorBreakdown): number {
  return b.bihub + b.bilbao + b.iknow + b.championsHub;
}

export function totalPriceUSD(b: SponsorBreakdown): number {
  return (
    b.bihub * SCHOLARSHIP_UNIT_PRICE_USD.bihub +
    b.bilbao * SCHOLARSHIP_UNIT_PRICE_USD.bilbao +
    b.iknow * SCHOLARSHIP_UNIT_PRICE_USD.iknow +
    b.championsHub * SCHOLARSHIP_UNIT_PRICE_USD.championsHub
  );
}

export function formatUSD(value: number, _locale: 'en' | 'ar' = 'en'): string {
  // Always Western digits; locale param kept for backwards compatibility.
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export const initiativeStats: { label: Bilingual; value: string; suffix?: Bilingual }[] = [
  { label: { en: 'Educational scholarships', ar: 'منحة تعليمية' }, value: '150' },
  { label: { en: 'Days to launch', ar: 'يوماً للإطلاق' }, value: '70' },
  { label: { en: 'International targets', ar: 'جهة دولية مستهدفة' }, value: '5+' },
  { label: { en: 'Barça scholarships', ar: 'منح برشلونة' }, value: '5' },
];

export const initiativePartners: InitiativePartner[] = [
  {
    id: 'champions-academies',
    name: { en: 'Champions Academies', ar: 'تشامبيونز أكاديمي' },
    role: {
      en: 'Initiative Lead — federations, governments, international institutions',
      ar: 'واجهة المبادرة الرسمية — الاتحادات والجهات الحكومية والمؤسسات الدولية',
    },
    scope: { en: 'Gaza · Palestine · Global', ar: 'غزة — فلسطين — جهات دولية' },
    emphasis: 'primary',
  },
  {
    id: 'al-jalaa',
    name: { en: 'Al-Jalaa Sports Club', ar: 'نادي الجلاء الرياضي' },
    role: {
      en: 'Co-host — registered non-profit anchoring on-the-ground delivery',
      ar: 'الشريك المؤسسي — جهة غير ربحية مسجلة تدعم التنفيذ الميداني',
    },
    scope: { en: 'Gaza Strip', ar: 'قطاع غزة' },
    emphasis: 'co-host',
  },
  {
    id: 'champions-hub',
    name: { en: 'Champions Hub', ar: 'Champions Hub' },
    role: {
      en: 'Technical & Educational Supporter — platform, courses, content',
      ar: 'الداعم التقني والتعليمي — المنصة والكورسات والمحتوى',
    },
    scope: { en: 'Arab world — independent identity', ar: 'الوطن العربي — هوية مستقلة' },
    emphasis: 'supporter',
  },
];

export const scholarshipSources: ScholarshipSource[] = [
  {
    id: 'bihub',
    name: { en: 'Barça Innovation Hub', ar: 'Barça Innovation Hub' },
    count: 5,
    pricePerUnitUSD: 750,
    platform: { en: 'Barcelona platform', ar: 'منصة برشلونة' },
    importance: {
      en: 'Highest tier — the headline draw of the initiative',
      ar: 'الأعلى — أداة الجذب الرئيسية للمبادرة',
    },
    website: 'https://barcainnovationhub.fcbarcelona.com',
    highlight: true,
  },
  {
    id: 'bilbao',
    name: { en: 'AC Football Center', ar: 'AC Football Center' },
    count: 30,
    pricePerUnitUSD: 400,
    platform: { en: 'Champions Hub', ar: 'Champions Hub' },
    importance: {
      en: 'Athletic Club Football Center — recognized European name reinforcing credibility',
      ar: 'Athletic Club Football Center — اسم أوروبي معروف يعزز المصداقية',
    },
    logo: '/logos/ac-football-center.png',
    logoTreatment: 'dark',
  },
  {
    id: 'iknow',
    name: { en: 'Ekkono Coaches Academy', ar: 'Ekkono Coaches Academy' },
    count: 30,
    pricePerUnitUSD: 500,
    platform: { en: 'Champions Hub', ar: 'Champions Hub' },
    importance: {
      en: 'Specialised methodology content with Arabic translation',
      ar: 'محتوى منهجي متخصص ومترجم بالعربية',
    },
    logo: '/logos/ekkono.png',
    logoTreatment: 'dark',
  },
  {
    id: 'champions-hub',
    name: { en: 'Champions Hub', ar: 'Champions Hub' },
    count: 85,
    pricePerUnitUSD: 150,
    platform: { en: 'Champions Hub', ar: 'Champions Hub' },
    importance: {
      en: 'Fully owned content — zero delivery cost',
      ar: 'ملكية كاملة — تكلفة تسليم صفر',
    },
    logo: '/logos/champions-hub-horizontal-light.png',
    logoTreatment: 'light',
    website: 'https://champions-hub.com',
  },
];

export const phases: Phase[] = [
  {
    id: 'foundation',
    range: { en: 'Days 1 – 15', ar: 'الأيام ١ إلى ١٥' },
    title: { en: 'Foundation', ar: 'التأسيس' },
    tracks: {
      institutional: {
        en: 'Initiative document drafted (AR/EN), Al-Jalaa file prepared, FIFA meeting briefed',
        ar: 'صياغة وثيقة المبادرة عربي/إنجليزي، إعداد ملف نادي الجلاء، تحضير اجتماع FIFA',
      },
      digital: {
        en: 'Visual identity, initiative page, 90-second launch video',
        ar: 'تصميم هوية المبادرة البصرية، بناء صفحة المبادرة، إنتاج فيديو الإطلاق (٩٠ ثانية)',
      },
      operational: {
        en: 'Egypt team roles, Gaza coordinators activated, scholarship costs computed',
        ar: 'توزيع أدوار فريق مصر، تفعيل التواصل مع منسقي غزة، احتساب تكاليف المنح',
      },
    },
  },
  {
    id: 'launch',
    range: { en: 'Days 16 – 35', ar: 'الأيام ١٦ إلى ٣٥' },
    title: { en: 'Launch', ar: 'الإطلاق' },
    tracks: {
      institutional: {
        en: 'Initiative document sent to 5 international bodies, partnership renewal talks',
        ar: 'إرسال وثيقة المبادرة لـ ٥ جهات دولية، نقاش تجديد الشراكات',
      },
      digital: {
        en: 'Registration campaign on platforms, 3 launch posts, launch video published',
        ar: 'إطلاق حملة التسجيل على المنصات، ٣ منشورات إطلاق، نشر فيديو الإطلاق',
      },
      operational: {
        en: 'Coach registration opens, applications filtered by license, accepted coaches start courses',
        ar: 'فتح التسجيل للمدربين، فلترة الطلبات بحسب الشهادات، إشعار المقبولين وبدء الكورسات',
      },
    },
  },
  {
    id: 'execution',
    range: { en: 'Days 36 – 55', ar: 'الأيام ٣٦ إلى ٥٥' },
    title: { en: 'Execution', ar: 'التنفيذ' },
    tracks: {
      institutional: {
        en: 'Mid-initiative report to international bodies, sponsorship negotiation, success stories documented',
        ar: 'تقرير منتصف المبادرة للجهات الدولية، التفاوض على دعم مالي أو شراكة، توثيق قصص نجاح المشاركين',
      },
      digital: {
        en: 'Weekly testimonials, BiHUB scholarship selection announced, ongoing social coverage',
        ar: 'محتوى أسبوعي: شهادات المشاركين، إعلان منح BiHUB وآلية الاختيار، تغطية مستمرة على السوشيال',
      },
      operational: {
        en: 'Weekly interactive webinars, mid-way participant evaluation, Barça scholarship shortlist',
        ar: 'جلسات تفاعلية أسبوعية (ويبينار)، تقييم منتصف الطريق للمشاركين، اختيار مرشحي منح برشلونة',
      },
    },
  },
  {
    id: 'closing',
    range: { en: 'Days 56 – 70', ar: 'الأيام ٥٦ إلى ٧٠' },
    title: { en: 'Closing & Impact', ar: 'الختام والأثر' },
    tracks: {
      institutional: {
        en: 'Final impact report, Phase 2 announcement, formal FIFA/BiHUB partnership framework',
        ar: 'تقرير الأثر النهائي للجهات الدولية، إعلان المرحلة الثانية، إطار شراكة رسمية مع FIFA/BiHUB',
      },
      digital: {
        en: 'Live closing ceremony, 5 Barça winners announced, visual impact report (numbers + stories)',
        ar: 'حفل ختامي افتراضي (بث مباشر)، إعلان الفائزين بمنح برشلونة الـ٥، تقرير مرئي: أرقام + قصص + أثر',
      },
      operational: {
        en: 'Certificates delivered, satisfaction survey, evaluation and Phase 2 planning',
        ar: 'تسليم الشهادات للمشاركين، استطلاع رضا + تغذية راجعة، تقييم المبادرة + تخطيط المرحلة ٢',
      },
    },
  },
];

export const sponsorPackages: SponsorPackage[] = [
  {
    id: 'diamond',
    name: { en: 'Diamond', ar: 'الماس' },
    totalGrants: 150,
    breakdown: { bihub: 5, bilbao: 30, iknow: 30, championsHub: 85 },
    priceUSD: 30000,
    badge: { en: 'Lead sponsor', ar: 'الراعي الرئيسي' },
    featured: true,
    benefits: [
      { en: 'Primary logo placement on initiative page & all materials', ar: 'الشعار الأول على صفحة المبادرة وجميع المواد' },
      { en: 'Logo on every issued certificate (100 certificates)', ar: 'الشعار على جميع الشهادات الصادرة (١٠٠ شهادة)' },
      { en: 'Joint press release at launch', ar: 'بيان صحفي مشترك عند الإطلاق' },
      { en: 'Exclusive impact report at closing ceremony', ar: 'تقرير أثر حصري في الحفل الختامي' },
      { en: 'Speaker slot at the live closing ceremony', ar: 'فقرة كلمة في الحفل الختامي المباشر' },
      { en: 'Featured sponsor in the final visual report', ar: 'راعٍ مميَّز في التقرير المرئي النهائي' },
    ],
  },
  {
    id: 'gold',
    name: { en: 'Gold', ar: 'الذهب' },
    totalGrants: 75,
    breakdown: { bihub: 3, bilbao: 15, iknow: 15, championsHub: 42 },
    priceUSD: 14700,
    badge: { en: 'Major sponsor', ar: 'راعٍ رئيسي' },
    benefits: [
      { en: 'Secondary logo placement across initiative materials', ar: 'الشعار الثاني عبر مواد المبادرة' },
      { en: 'Logo on its 50 issued certificates', ar: 'الشعار على شهاداته الـ٥٠' },
      { en: 'Mention in all primary marketing materials', ar: 'ذكر في المواد التسويقية الرئيسية' },
      { en: 'Branded slot in the impact report', ar: 'فقرة باسم الراعي في تقرير الأثر' },
      { en: 'Mid-initiative thank-you content piece', ar: 'محتوى شكر في منتصف المبادرة' },
    ],
  },
  {
    id: 'silver',
    name: { en: 'Silver', ar: 'الفضة' },
    totalGrants: 37,
    breakdown: { bihub: 0, bilbao: 7, iknow: 7, championsHub: 23 },
    priceUSD: 6750,
    badge: { en: 'Supporting sponsor', ar: 'راعٍ مساند' },
    benefits: [
      { en: 'Logo on the dedicated sponsors page', ar: 'الشعار في صفحة الرعاة المخصصة' },
      { en: 'Logo on its 25 issued certificates', ar: 'الشعار على شهاداته الـ٢٥' },
      { en: 'Mention in the impact report', ar: 'ذكر في تقرير الأثر' },
      { en: 'Social media thank-you post', ar: 'منشور شكر على السوشيال ميديا' },
    ],
  },
];

export const internationalTargets: InternationalTarget[] = [
  {
    id: 'fifa',
    name: { en: 'FIFA', ar: 'الفيفا' },
    domain: { en: 'Sport + humanitarian', ar: 'رياضة + إنسانية' },
    entryPoint: { en: 'Upcoming meeting — top priority', ar: 'اجتماع قريب — أولوية قصوى' },
    status: { en: 'Engaged', ar: 'في التواصل' },
  },
  {
    id: 'bihub',
    name: { en: 'Barça Innovation Hub', ar: 'Barça Innovation Hub' },
    domain: { en: 'Sport + education', ar: 'رياضة + تعليم' },
    entryPoint: {
      en: 'Existing 5-year partnership; renewal late 2026',
      ar: 'شراكة قائمة منذ ٥ سنوات — تجديد نهاية ٢٠٢٦',
    },
    status: { en: 'Open negotiation window', ar: 'نافذة مفتوحة للتفاوض' },
  },
  {
    id: 'unicef',
    name: { en: 'UNICEF & UN Agencies', ar: 'يونيسف ووكالات الأمم المتحدة' },
    domain: { en: 'Children + education + humanitarian', ar: 'طفل + تعليم + إنسانية' },
    entryPoint: {
      en: 'Through Al-Jalaa as registered non-profit',
      ar: 'عبر نادي الجلاء كجهة غير ربحية مسجلة',
    },
    status: { en: 'Formal submission in Phase 2', ar: 'تقديم رسمي خلال المرحلة الثانية' },
  },
  {
    id: 'gulf',
    name: { en: 'Gulf & Arab organisations', ar: 'منظمات خليجية وعربية' },
    domain: { en: 'Regional support', ar: 'دعم إقليمي' },
    entryPoint: { en: 'Direct outreach via partnerships team', ar: 'التواصل المباشر عبر قسم الشراكات' },
    status: { en: 'Initial research & outreach', ar: 'مرحلة البحث والتواصل الأولى' },
  },
];

export const kpiGroups: KpiGroup[] = [
  {
    id: 'operations',
    title: { en: 'Operations', ar: 'التشغيل' },
    items: [
      { en: '100 coaches registered and accepted', ar: '١٠٠ مدرب مسجل ومقبول' },
      { en: 'Initiative page live within 15 days', ar: 'صفحة المبادرة جاهزة خلال ١٥ يوماً' },
      { en: '3 marketing posts per week', ar: '٣ منشورات تسويقية أسبوعياً' },
      { en: 'One professional launch video', ar: 'فيديو إطلاق واحد احترافي' },
      { en: 'Zero platform downtime', ar: 'صفر تعطل في المنصة' },
    ],
  },
  {
    id: 'impact',
    title: { en: 'Impact', ar: 'الأثر' },
    items: [
      { en: '5 Barça scholarships delivered to top performers', ar: '٥ منح برشلونة تُسلَّم للمتميزين' },
      { en: 'Live closing broadcast with testimonials', ar: 'بث ختامي مباشر مع شهادات' },
      { en: 'Documented impact report for international bodies', ar: 'تقرير أثر موثق للجهات الدولية' },
      { en: 'Video success stories', ar: 'قصص نجاح موثقة بالفيديو' },
      { en: 'Phase 2 announced', ar: 'إعلان المرحلة الثانية' },
    ],
  },
  {
    id: 'sustainability',
    title: { en: 'Sustainability', ar: 'الاستدامة' },
    items: [
      { en: '5 international bodies formally engaged', ar: '٥ جهات دولية مقدَّم لها رسمياً' },
      { en: 'At least one sponsorship agreement', ar: 'اتفاقية رعاية واحدة على الأقل' },
      { en: 'Pricing model ready within 10 days', ar: 'نموذج تسعير جاهز خلال ١٠ أيام' },
      { en: 'Partnership framework with FIFA or BiHUB', ar: 'إطار شراكة مع FIFA أو BiHUB' },
      { en: 'Sustained employment for the team', ar: 'تشغيل مستمر لكوادر الفريق' },
    ],
  },
];

export const sponsorFaq: FaqItem[] = [
  {
    question: {
      en: 'How do I get a quote for a package?',
      ar: 'كيف أحصل على عرض للحزمة؟',
    },
    answer: {
      en: 'Send us your selected package and any custom configuration through the form below. Our partnerships team will reply within 48 hours with full scope, deliverables, and tailored details for your organisation.',
      ar: 'أرسل لنا الحزمة المختارة أو تركيبتك المخصصة عبر النموذج أدناه. سيتواصل معك فريق الشراكات خلال 48 ساعة لإرسال نطاق الرعاية الكامل والتفاصيل المخصصة لمؤسستك.',
    },
  },
  {
    question: { en: 'Where will my logo appear?', ar: 'أين سيظهر شعاري كراعٍ؟' },
    answer: {
      en: 'On the initiative page, the campaign pages, the certificates issued under your tier, and the final impact report — placement order depends on your selected package.',
      ar: 'على صفحة المبادرة، صفحات الحملة، الشهادات الصادرة ضمن حزمتك، والتقرير النهائي للأثر — ترتيب الظهور بحسب الحزمة المختارة.',
    },
  },
  {
    question: { en: 'Do you accept in-kind sponsorship?', ar: 'هل تقبلون رعاية عينية؟' },
    answer: {
      en: 'Yes, in-kind contributions (training equipment, media production, content licensing) can be evaluated against the package scope on a case-by-case basis.',
      ar: 'نعم، يمكن تقييم المساهمات العينية (معدات تدريب، إنتاج إعلامي، رخص محتوى) مقابل نطاق الحزمة لكل حالة على حدة.',
    },
  },
  {
    question: { en: 'When does the initiative start?', ar: 'متى تبدأ المبادرة؟' },
    answer: {
      en: 'The initiative runs over 70 days from official launch. Sponsorship onboarding can begin immediately during the foundation phase (days 1–15).',
      ar: 'تمتد المبادرة على ٧٠ يوماً من الإطلاق الرسمي، ويمكن بدء انضمام الرعاة فوراً في مرحلة التأسيس (الأيام ١–١٥).',
    },
  },
];

export const coachFaq: FaqItem[] = [
  {
    question: { en: 'Who is eligible to apply?', ar: 'من يحق له التقدم؟' },
    answer: {
      en: 'Football coaches currently residing in Gaza, holding a valid coaching license issued by FIFA or the Palestinian Football Federation, at any level from PRO to D.',
      ar: 'مدربو كرة القدم المقيمون داخل قطاع غزة حالياً، الحاصلون على ترخيص ساري من الاتحاد الدولي أو الاتحاد الفلسطيني، من المستوى PRO إلى D.',
    },
  },
  {
    question: { en: 'Are scholarships really free?', ar: 'هل المنح مجانية فعلاً؟' },
    answer: {
      en: 'Yes — every accepted coach receives the scholarship at zero cost. The initiative is funded by partners and sponsors.',
      ar: 'نعم — كل مدرب مقبول يحصل على المنحة دون أي تكلفة، والمبادرة ممولة من الشركاء والرعاة.',
    },
  },
  {
    question: { en: 'Which courses do I receive?', ar: 'ما الكورسات التي سأحصل عليها؟' },
    answer: {
      en: 'Selected scholarships from Barça Innovation Hub, AC Football Center, Ekkono Coaches Academy, and Champions Hub. Specific assignment depends on your level and selection round.',
      ar: 'منح مختارة من Barça Innovation Hub وAC Football Center وEkkono Coaches Academy وChampions Hub. التوزيع حسب مستواك وجولة الاختيار.',
    },
  },
  {
    question: { en: 'How are the 5 Barça scholarships allocated?', ar: 'كيف تُوزَّع منح برشلونة الـ٥؟' },
    answer: {
      en: 'They are awarded to the top-performing participants at the end of the initiative, based on engagement, completion, and a final evaluation.',
      ar: 'تُمنح للمتميزين في نهاية المبادرة بناءً على التفاعل وإتمام الكورسات والتقييم النهائي.',
    },
  },
  {
    question: { en: 'What if I do not have a license document copy?', ar: 'ماذا لو لم أملك نسخة من شهادة الترخيص؟' },
    answer: {
      en: 'Submit the form with your license number; our Gaza coordinators will contact you to verify with the federation directly.',
      ar: 'قدّم الطلب برقم الترخيص فقط، وسيتواصل معك منسقو غزة للتحقق مع الاتحاد مباشرة.',
    },
  },
];
